import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { ChefHat, Loader2, Building2 } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [restaurantCode, setRestaurantCode] = useState('');
  const [isLookingUpRestaurant, setIsLookingUpRestaurant] = useState(false);

  const {
    login,
    isAuthenticated,
    fetchRestaurantBySlug,
    selectRestaurant,
    clearRestaurant,
    selectedRestaurantId,
    selectedRestaurantName,
    isLoadingRestaurants,
    sessionExpired,
  } = useAuthStore();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Check for restaurant ID/slug in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const restaurantParam = params.get('r');

    if (restaurantParam) {
      // Look up restaurant by slug or ID
      fetchRestaurantBySlug(restaurantParam);
    }
  }, [fetchRestaurantBySlug]);

  const hasRestaurant = !!selectedRestaurantId;

  const handlePinInput = (digit: string) => {
    if (!hasRestaurant) {
      setError('Please select a restaurant first');
      return;
    }
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      setError('');

      // Auto-submit only when PIN reaches max length (6 digits)
      if (newPin.length === 6) {
        submitPin(newPin);
      }
    }
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  const handleBackspace = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const submitPin = async (pinToSubmit: string) => {
    if (pinToSubmit.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await login(pinToSubmit);

    setIsLoading(false);

    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setError(result.error || 'Invalid PIN');
      setPin('');
    }
  };

  const handleSubmit = async () => {
    submitPin(pin);
  };

  const handleRestaurantCodeSubmit = async () => {
    if (!restaurantCode.trim()) {
      setError('Please enter a restaurant code');
      return;
    }
    setIsLookingUpRestaurant(true);
    setError('');
    const found = await fetchRestaurantBySlug(restaurantCode.trim());
    setIsLookingUpRestaurant(false);
    if (!found) {
      setError('Restaurant not found. Check your code and try again.');
    }
  };

  const handleChangeRestaurant = () => {
    clearRestaurant();
    setRestaurantCode('');
    setPin('');
    setError('');
    // Clear URL param
    window.history.replaceState({}, '', window.location.pathname);
  };

  const numpadButtons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['C', '0', '<'],
  ];

  return (
    // Scrollable + 1px-taller inner wrapper = iOS rubber-band bounce even
    // though the content fits the screen
    <div className="h-[100dvh] overflow-y-auto overscroll-contain bg-slate-900">
      <div className="min-h-[calc(100%+1px)] flex items-center justify-center p-4 safe-y safe-x">
      <div className="bg-slate-800 rounded-2xl p-6 sm:p-8 w-full max-w-sm shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ChefHat className="w-10 h-10 text-white" />
            <img src="/mise-wordmark.svg" alt="mise" className="h-7 invert" />
          </div>
          <p className="text-lg font-medium text-slate-300">Kitchen Display</p>
          <p className="text-slate-400 mt-2 text-base">Enter your PIN to continue</p>
        </div>

        {sessionExpired && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-slate-700 text-slate-200 text-sm text-center">
            Your session ended — please sign in again to keep going.
          </div>
        )}

        {/* Loading state */}
        {isLoadingRestaurants && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
            <span className="ml-2 text-slate-400">Loading...</span>
          </div>
        )}

        {/* Restaurant Code Entry (when no restaurant selected) */}
        {!isLoadingRestaurants && !hasRestaurant && (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <Building2 className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-white">Enter Restaurant Code</h2>
              <p className="text-sm text-slate-400 mt-1">
                Your manager will provide this code
              </p>
            </div>

            <input
              type="text"
              value={restaurantCode}
              onChange={(e) => setRestaurantCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleRestaurantCodeSubmit()}
              placeholder="restaurant-code"
              className="w-full px-4 py-3 text-center text-lg font-mono bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
            />

            {error && (
              <p className="text-center text-red-400 text-sm">{error}</p>
            )}

            <button
              onClick={handleRestaurantCodeSubmit}
              disabled={isLookingUpRestaurant || !restaurantCode.trim()}
              className="w-full py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg active:bg-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLookingUpRestaurant ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Looking up...
                </span>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        )}

        {/* PIN Entry (when restaurant is selected) */}
        {!isLoadingRestaurants && hasRestaurant && (
          <>
            {/* Restaurant indicator */}
            <div className="mb-6 flex items-center justify-between px-4 py-3 bg-slate-700 rounded-xl">
              <div>
                <span className="text-xs text-slate-400 uppercase tracking-wide">Restaurant</span>
                <p className="font-medium text-white">{selectedRestaurantName}</p>
              </div>
              <button
                onClick={handleChangeRestaurant}
                className="text-sm text-primary-400 hover:text-primary-300 font-medium"
              >
                Change
              </button>
            </div>

            {/* PIN Display */}
            <div className="flex justify-center gap-3 sm:gap-4 mb-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full transition-all ${
                    i < pin.length ? 'bg-primary-500 scale-110' : 'bg-slate-600'
                  }`}
                />
              ))}
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-red-400 text-center text-base mb-4 font-medium">{error}</div>
            )}

            {/* Numpad - large touch targets for kitchen environment */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {numpadButtons.flat().map((btn) => (
                <button
                  key={btn}
                  onClick={() => {
                    if (btn === 'C') handleClear();
                    else if (btn === '<') handleBackspace();
                    else handlePinInput(btn);
                  }}
                  disabled={isLoading || isLoadingRestaurants}
                  className={`h-16 sm:h-18 rounded-xl text-2xl sm:text-3xl font-bold transition-all btn-press min-h-[64px] ${
                    btn === 'C'
                      ? 'bg-red-600/20 text-red-400 active:bg-red-600/40'
                      : btn === '<'
                      ? 'bg-slate-700 text-slate-300 active:bg-slate-500'
                      : 'bg-slate-700 text-white active:bg-slate-500'
                  } disabled:opacity-50`}
                >
                  {btn === '<' ? '⌫' : btn}
                </button>
              ))}
            </div>

            {/* Login button - shows when PIN is 4-5 digits */}
            {pin.length >= 4 && pin.length < 6 && !isLoading && (
              <button
                onClick={handleSubmit}
                className="w-full h-16 bg-primary-600 active:bg-primary-700 text-white rounded-xl text-xl font-bold transition-all btn-press flex items-center justify-center gap-2"
              >
                Sign In
              </button>
            )}

            {isLoading && (
              <div className="flex items-center justify-center h-16">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                <span className="ml-2 text-slate-400">Signing in...</span>
              </div>
            )}

            {/* Hint */}
            <p className="text-slate-500 text-sm text-center mt-4">
              Use your employee PIN to access the kitchen display
            </p>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
